import { useEffect, useState } from 'react';
import { TestValueWrapper } from '../utils/TestValueWrapper';

type Props = {
  initial?: number;
  value: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
  duration?: number;
  steps?: number;
  disableAnimation?: boolean;
};

/**
 * Component to animate counting between two numerical states.
 *
 * Number will be animated between current value and new value with linear animation speed.
 */
export const AnimatedNumber = ({
  initial = 0.0,
  value,
  suffix = '',
  prefix = '',
  decimals = 0,
  duration = 300,
  steps = 12,
  disableAnimation = false,
}: Props) => {
  const [displayValue, setDisplayValue] = useState(disableAnimation ? Number(value) : Number(initial));

  useEffect(() => {
    // Last step sets the actual value in the state, so we can depend on this without float precision checking.
    if (value === displayValue || disableAnimation) {
      return;
    }

    const wrappedDuration = TestValueWrapper.wrap(duration, 0);
    const wrappedSteps = TestValueWrapper.wrap(steps, 1);

    let step = 0;
    const stepDiff = (value - displayValue) / wrappedSteps;
    const intervalValue = setInterval(() => {
      if (step >= wrappedSteps) {
        // Cast to number so that we don't run into any issues with integers and using .toFixed.
        setDisplayValue(Number(value));
        clearInterval(intervalValue);
        return;
      }

      step += 1;
      setDisplayValue((value) => value + stepDiff);
    }, wrappedDuration / steps);

    // If unmounted or if value changes, start new cycle.
    return () => clearInterval(intervalValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, disableAnimation]);

  return (
    <>
      {prefix}
      {displayValue.format({ decimals })}
      {suffix}
    </>
  );
};
